/**
 * Team Management Page
 * 
 * Main page for managing team members with invite, edit, deactivate, and view activity
 * Part of E007-S026: Team Management
 */

import React, { useState, useEffect } from 'react';
import { useTeamManagement } from '../hooks/useTeamManagement';
import { TeamMember, UsersFilters } from '../types/team';
import { RoleBadge } from '../components/badges/RoleBadge';
import { TeamStatusBadge } from '../components/badges/TeamStatusBadge';
import { InviteUserModal } from '../components/modals/InviteUserModal';
import { EditUserModal } from '../components/modals/EditUserModal';
import { ActivityLogModal } from '../components/modals/ActivityLogModal';

export const TeamManagementPage: React.FC = () => {
  const { users, loading, error, totalCount, refresh, invite, update, deactivate, getActivity } = useTeamManagement();
  
  // State
  const [filters, setFilters] = useState<UsersFilters>({ page: 1, limit: 50 });
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Load users on mount and when filters change
  useEffect(() => {
    refresh(filters);
  }, [filters.page, filters.limit, filters.role, filters.status, filters.search, refresh]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchTerm || undefined, page: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, filters.search]);

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handlers
  const handleInvite = async (data: any) => {
    try {
      const response = await invite(data);
      showToast('Invitation sent successfully!', 'success');
      setShowInviteModal(false);
      return response;
    } catch (err) {
      showToast('Failed to send invitation', 'error');
      throw err;
    }
  };

  const handleUpdate = async (userId: string, data: any) => {
    try {
      await update(userId, data);
      showToast('User updated successfully!', 'success');
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err) {
      showToast('Failed to update user', 'error');
      throw err;
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await deactivate(userId);
      showToast('User deactivated successfully!', 'success');
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err) {
      showToast('Failed to deactivate user', 'error');
      throw err;
    }
  };

  const handleEditClick = (user: TeamMember) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleActivityClick = (user: TeamMember) => {
    setSelectedUser(user);
    setShowActivityModal(true);
  };

  const handleRoleFilter = (role: string) => {
    setFilters({ ...filters, role: role === 'all' ? undefined : role as any, page: 1 });
  };

  const handleStatusFilter = (status: string) => {
    setFilters({ ...filters, status: status === 'all' ? undefined : status as any, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 50));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
        <p className="text-gray-600 mt-1">Manage team members, roles, and permissions</p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <select
              value={filters.role || 'all'}
              onChange={(e) => handleRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status || 'all'}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </div>

          {/* Invite Button */}
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            + Invite User
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading team members...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && users.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Users Found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filters.role || filters.status
              ? 'No users match your filters. Try adjusting your search criteria.'
              : 'Get started by inviting your first team member.'}
          </p>
          {!searchTerm && !filters.role && !filters.status && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Invite Your First Team Member
            </button>
          )}
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && users.length > 0 && (
        <>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TeamStatusBadge status={user.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_active
                        ? new Date(user.last_active).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleActivityClick(user)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Activity
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm p-4 mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(filters.page! - 1) * filters.limit! + 1}</span> to{' '}
                <span className="font-medium">{Math.min(filters.page! * filters.limit!, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={filters.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {filters.page} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={filters.page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {}}
        onInvite={handleInvite}
      />

      <EditUserModal
        isOpen={showEditModal}
        user={selectedUser}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {}}
        onUpdate={handleUpdate}
        onDeactivate={handleDeactivate}
      />

      <ActivityLogModal
        isOpen={showActivityModal}
        userId={selectedUser?.id || ''}
        userName={selectedUser?.full_name || ''}
        onClose={() => {
          setShowActivityModal(false);
          setSelectedUser(null);
        }}
        onFetchActivity={getActivity}
      />
    </div>
  );
};
